import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Modal from '../../Modal';
import Section from '../../Section';
import HelpArticleLink from '../HelpArticleLink';
import AffiliateCodeForm from './AffiliateCodeForm';
import AffiliateCodesTable from './AffiliateCodesTable';
import AffiliateEarningsPanel from './AffiliateEarningsPanel';
import './Affiliates.css';

async function responseData(response, fallback) {
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || fallback);
  return body.data;
}

export default function AffiliatesManagerSection({ planOptions }) {
  const auth = useAuth();
  const [codes, setCodes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [loadedCodes, loadedSummary] = await Promise.all([
        auth.fetch(`${auth.API_URL}/v1/seller/affiliates/codes`)
          .then(response => responseData(response, 'Failed to load affiliate codes.')),
        auth.fetch(`${auth.API_URL}/v1/seller/affiliates/summary`)
          .then(response => responseData(response, 'Failed to load affiliate earnings.')),
      ]);
      setCodes(loadedCodes);
      setSummary(loadedSummary);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [auth.API_URL, auth.fetch]);

  useEffect(() => {
    load();
  }, [load]);

  const performanceByCode = useMemo(() => Object.fromEntries(
    (summary?.codes || []).map(row => [row.code.id, row]),
  ), [summary]);

  const saveCode = async (payload, code = null) => {
    setSaving(true);
    setError(null);
    try {
      const url = code
        ? `${auth.API_URL}/v1/seller/affiliates/codes/${code.uuid}`
        : `${auth.API_URL}/v1/seller/affiliates/codes`;
      const response = await auth.fetch(url, {
        method: code ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
      });
      await responseData(response, 'Failed to save affiliate code.');
      setEditingCode(null);
      setShowCreate(false);
      setNotice(code ? 'Affiliate code updated.' : 'Affiliate code created.');
      await load();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (code, status) => {
    setError(null);
    try {
      const response = await auth.fetch(
        `${auth.API_URL}/v1/seller/affiliates/codes/${code.uuid}/status`,
        { method: 'POST', body: JSON.stringify({ status }) },
      );
      await responseData(response, 'Failed to change affiliate code status.');
      setNotice(`Affiliate code ${status === 'active' ? 'activated' : 'paused'}.`);
      await load();
    } catch (statusError) {
      setError(statusError.message);
    }
  };

  return (
    <Section
      id="affiliates-manager"
      title="Affiliate codes"
      subtitle="Refer other Mayhem sellers and earn commission on eligible plan payments."
      preview="Create one code that works across monthly and annual seller plans."
    >
      <div className="affiliates-manager">
        <HelpArticleLink
          slug="affiliate-codes"
          label="Learn about affiliate codes"
          variant="link"
        />
        {notice && <div className="flash success" role="status">{notice}</div>}
        {error && <div className="flash error" role="alert">{error}</div>}
        {loading ? (
          <p>Loading affiliate program…</p>
        ) : (
          <>
            <AffiliateEarningsPanel summary={summary} />
            {codes.length ? (
              <AffiliateCodesTable
                codes={codes}
                performanceByCode={performanceByCode}
                onEdit={setEditingCode}
                onStatus={changeStatus}
              />
            ) : (
              <p>You have not created an affiliate code yet.</p>
            )}
            <button className="button primary" type="button" onClick={() => setShowCreate(true)}>
              Create affiliate code
            </button>
          </>
        )}
      </div>

      {(showCreate || editingCode) && (
        <Modal
          isOpen
          size="large"
          title={editingCode ? 'Edit affiliate code' : 'Create affiliate code'}
          onClose={() => {
            setEditingCode(null);
            setShowCreate(false);
          }}
        >
          <AffiliateCodeForm
            key={editingCode?.uuid || 'new'}
            code={editingCode}
            planOptions={planOptions}
            submitting={saving}
            onSubmit={payload => saveCode(payload, editingCode)}
            onCancel={() => {
              setEditingCode(null);
              setShowCreate(false);
            }}
          />
        </Modal>
      )}
    </Section>
  );
}
